import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { BACKEND_URL } from '@/lib/api';
import { Plus, Trash2, Copy, Calendar, TrendingUp, ChevronDown, ChevronUp, Eye, X, Upload, Download, FileSpreadsheet, Check, AlertTriangle } from 'lucide-react';
import MissingSalesDaysPanel from '@/components/MissingSalesDaysPanel';

const fmtCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  return Number(v).toLocaleString('en-IN');
};

export default function DailySalesTab({ divisionId: propDivId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [entries, setEntries] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date(); now.setHours(now.getHours() + 5); now.setMinutes(now.getMinutes() + 30);
    return now.toISOString().split('T')[0];
  });
  const [form, setForm] = useState({ outlet: '', amount: '', notes: '', sub_amounts: {}, occupancy: {} });
  const [showAgg, setShowAgg] = useState(true);
  const [aggPeriod, setAggPeriod] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [drillOutlet, setDrillOutlet] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showTdbImport, setShowTdbImport] = useState(false);
  const [tdbImportResult, setTdbImportResult] = useState(null);
  const [tdbImporting, setTdbImporting] = useState(false);
  const fileRef = useRef(null);
  const tdbFileRef = useRef(null);

  const canImport = user?.role === 'ceo' || user?.role === 'leader';

  const divId = propDivId || user?.division_id;
  const selectedOutletData = outlets.find(o => o.name === form.outlet);
  const subCats = selectedOutletData?.sub_categories || [];
  const hasOccupancy = selectedOutletData?.has_occupancy;
  const roomConfig = selectedOutletData?.room_config || {};

  const refresh = async () => {
    if (!divId) return;
    const [oRes, eRes, aRes] = await Promise.all([
      api.get(`/daily-sales/outlets-for-entry?division_id=${divId}`),
      api.get(`/daily-sales?division_id=${divId}&date_from=${selectedDate}&date_to=${selectedDate}`),
      api.get(`/daily-sales/aggregates?division_id=${divId}&period=${aggPeriod}`),
    ]);
    setOutlets(oRes.data);
    setEntries(eRes.data);
    setAggregates(aRes.data);
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [divId, selectedDate, aggPeriod]);

  const handleOutletChange = (outletName) => {
    setForm(prev => ({ ...prev, outlet: outletName, amount: '', sub_amounts: {}, occupancy: {} }));
  };

  // Auto-calculate total from sub-amounts
  const calcTotal = (subs) => Object.values(subs).reduce((s, v) => s + (parseFloat(v) || 0), 0);

  const handleSubAmountChange = (cat, value) => {
    setForm(prev => {
      const newSubs = { ...prev.sub_amounts, [cat]: value };
      const total = calcTotal(newSubs);
      return { ...prev, sub_amounts: newSubs, amount: total > 0 ? String(total) : prev.amount };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.outlet || !form.amount) return;
    const payload = {
      date: selectedDate, division_id: divId, outlet: form.outlet,
      amount: parseFloat(form.amount), notes: form.notes,
      sub_amounts: Object.fromEntries(Object.entries(form.sub_amounts).filter(([, v]) => v && parseFloat(v) > 0).map(([k, v]) => [k, parseFloat(v)])),
    };
    if (hasOccupancy && (form.occupancy.suites_occupied || form.occupancy.deluxe_occupied)) {
      payload.occupancy = {
        suites_occupied: parseInt(form.occupancy.suites_occupied) || 0,
        deluxe_occupied: parseInt(form.occupancy.deluxe_occupied) || 0,
        total_occupied: (parseInt(form.occupancy.suites_occupied) || 0) + (parseInt(form.occupancy.deluxe_occupied) || 0),
        total_rooms: roomConfig.total_rooms || 10,
      };
    }
    await api.post('/daily-sales', payload);
    setForm({ outlet: form.outlet, amount: '', notes: '', sub_amounts: {}, occupancy: {} });
    refresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await api.delete(`/daily-sales/${id}`);
    refresh();
  };

  const repeatYesterday = async () => {
    try {
      const { data } = await api.get(`/daily-sales/last-pattern?division_id=${divId}`);
      if (data.entries?.length) {
        for (const e of data.entries) {
          await api.post('/daily-sales', { ...e, date: selectedDate, division_id: divId });
        }
        refresh();
      } else { alert('No entries found for yesterday'); }
    } catch { alert('Could not load yesterday pattern'); }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BACKEND_URL}/api/daily-sales/csv-template?division_id=${divId}&token=${token}`, '_blank');
  };

  const downloadExport = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BACKEND_URL}/api/daily-sales/export?division_id=${divId}&token=${token}`, '_blank');
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('division_id', divId);
      const { data } = await api.post('/daily-sales/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      if (data.imported > 0) refresh();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const downloadTdbTemplate = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BACKEND_URL}/api/daily-sales/tdb-channels-template?token=${token}`, '_blank');
  };

  const handleTdbImport = async (file) => {
    if (!file) return;
    setTdbImporting(true);
    setTdbImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/daily-sales/tdb-channels/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTdbImportResult(data);
      if ((data.imported || 0) + (data.updated || 0) > 0) refresh();
    } catch (err) {
      setTdbImportResult({ error: err?.response?.data?.detail || 'Import failed' });
    } finally {
      setTdbImporting(false);
      if (tdbFileRef.current) tdbFileRef.current.value = '';
    }
  };

  const openDrill = async (outletName) => {
    setDrillOutlet(outletName);
    try {
      const r = await api.get(`/daily-sales/outlet-detail?division_id=${divId}&outlet=${encodeURIComponent(outletName)}`);
      setDrillData(r.data);
    } catch { setDrillData(null); }
  };

  const todayTotal = entries.reduce((s, e) => s + (e.amount || 0), 0);

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24" data-testid="daily-sales-page">
      {/* Missing-days audit — visible at-a-glance so the leader can */}
      {/* nudge the ops team without hunting through pages. */}
      <MissingSalesDaysPanel divisionFilter={divId} />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Daily Sales Entry</h2>
          <p className="text-xs" style={{ color: '#6B7280' }}>Log sales as they happen. Sub-category breakdown auto-rolls up.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadExport} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] border hover:bg-gray-50"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="export-csv-btn">
            <Download size={12} /> Export
          </button>
          {canImport && (
            <button onClick={() => setShowImport(!showImport)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
              style={{ backgroundColor: '#2A7B88' }} data-testid="import-csv-btn">
              <Upload size={12} /> Import CSV
            </button>
          )}
          {canImport && divId === 'tdb' && (
            <button onClick={() => setShowTdbImport(!showTdbImport)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-white"
              style={{ backgroundColor: '#1B2A4A' }} data-testid="import-tdb-channels-btn"
              title="Upload Zomato/Swiggy/Store/B2B/Delivery Charges. Shopify Online stays auto-synced.">
              <Upload size={12} /> TDB Channels
            </button>
          )}
        </div>
      </div>

      {/* CSV Import Panel */}
      {showImport && canImport && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#2A7B88', borderWidth: '1.5px' }} data-testid="import-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={16} style={{ color: '#2A7B88' }} />
              <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Import Daily Sales from CSV</h3>
            </div>
            <button onClick={() => { setShowImport(false); setImportResult(null); }}><X size={16} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={downloadTemplate} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs border hover:bg-gray-50"
              style={{ borderColor: '#E5E1DC', color: '#2A7B88' }} data-testid="download-template-btn">
              <Download size={13} /> Download Template
            </button>
            <div className="flex-1">
              <input ref={fileRef} type="file" accept=".csv" onChange={e => handleImport(e.target.files?.[0])}
                className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:text-white file:cursor-pointer w-full"
                style={{ color: '#6B7280' }}
                data-testid="csv-file-input" />
            </div>
          </div>
          <div className="text-[10px] space-y-0.5" style={{ color: '#9CA3AF' }}>
            <p>Required columns: <strong>date</strong>, <strong>outlet</strong> (exact name), <strong>amount</strong> (INR)</p>
            <p>Accepted date formats: <strong>YYYY-MM-DD</strong>, <strong>DD-MM-YYYY</strong>, <strong>DD/MM/YYYY</strong></p>
            <p>Optional: sub-category columns (Suite, Deluxe, etc.), suites_occupied, deluxe_occupied, total_rooms, notes</p>
            <p>Re-importing the same (date, outlet) updates the existing row instead of duplicating.</p>
          </div>
          {importing && <p className="text-xs font-medium" style={{ color: '#2A7B88' }}>Importing...</p>}
          {importResult && !importResult.error && (
            <div className="rounded-md p-3" style={{ backgroundColor: (importResult.imported + (importResult.updated || 0)) > 0 ? '#ECFDF5' : '#FEF3C7' }}>
              <div className="flex items-center gap-2">
                <Check size={14} style={{ color: '#2D8B5E' }} />
                <span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>
                  {importResult.imported} new{importResult.updated ? `, ${importResult.updated} updated` : ''}{importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ''}
                </span>
              </div>
              {importResult.by_division && Object.keys(importResult.by_division).length > 1 && (
                <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>
                  Routed to: {Object.entries(importResult.by_division).map(([d, n]) => `${d} (${n})`).join(', ')}
                </p>
              )}
              {importResult.errors?.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {importResult.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-[10px] flex items-center gap-1" style={{ color: '#D4726A' }}>
                      <AlertTriangle size={10} /> Row {e.row}: {e.error}
                    </p>
                  ))}
                  {importResult.errors.length > 5 && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>+{importResult.errors.length - 5} more errors</p>}
                </div>
              )}
            </div>
          )}
          {importResult?.error && (
            <p className="text-xs" style={{ color: '#D4726A' }}>{importResult.error}</p>
          )}
        </div>
      )}

      {/* TDB Channels Import Panel — only the manual non-Shopify channels */}
      {showTdbImport && canImport && divId === 'tdb' && (
        <div className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#1B2A4A', borderWidth: '1.5px' }} data-testid="tdb-import-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSpreadsheet size={16} style={{ color: '#1B2A4A' }} />
              <h3 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>TDB — Upload Zomato / Swiggy / Store / B2B / Delivery Charges</h3>
            </div>
            <button onClick={() => { setShowTdbImport(false); setTdbImportResult(null); }}><X size={16} style={{ color: '#6B7280' }} /></button>
          </div>
          <div className="rounded-md p-2.5" style={{ backgroundColor: '#F4F8FA' }}>
            <p className="text-[11px] leading-relaxed" style={{ color: '#1B2A4A' }}>
              <strong>Shopify "Online" stays untouched</strong> — it auto-syncs from the webhook. You only fill the manual channels here. Per (date, outlet) the existing Online value is preserved and your numbers are merged in.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={downloadTdbTemplate} className="flex items-center gap-1 px-3 py-2 rounded-md text-xs border hover:bg-gray-50"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }} data-testid="download-tdb-template-btn">
              <Download size={13} /> Download Template
            </button>
            <div className="flex-1">
              <input ref={tdbFileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={e => handleTdbImport(e.target.files?.[0])}
                className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:text-white file:cursor-pointer w-full"
                style={{ color: '#6B7280' }}
                data-testid="tdb-channels-file-input" />
            </div>
          </div>
          <div className="text-[10px] space-y-0.5" style={{ color: '#9CA3AF' }}>
            <p>Accepted formats: <strong>.csv</strong> (long format, our template) or <strong>.xlsx</strong> (the wide TDC monthly sheet — auto-detected).</p>
            <p>CSV columns: <strong>date</strong>, <strong>outlet</strong> (Bengaluru / Mumbai / Gurgaon), <strong>Store</strong>, <strong>Swiggy</strong>, <strong>Zomato</strong>, <strong>Delivery Charges</strong>, <strong>B2B</strong>, notes.</p>
            <p>Re-uploading the same file is a safe no-op — rows that already match are skipped.</p>
          </div>
          {tdbImporting && <p className="text-xs font-medium" style={{ color: '#1B2A4A' }}>Importing & merging with Shopify Online…</p>}
          {tdbImportResult && !tdbImportResult.error && (
            <div className="rounded-md p-3" style={{ backgroundColor: ((tdbImportResult.imported || 0) + (tdbImportResult.updated || 0)) > 0 ? '#ECFDF5' : '#FEF3C7' }}>
              <div className="flex items-center gap-2">
                <Check size={14} style={{ color: '#2D8B5E' }} />
                <span className="text-xs font-medium" style={{ color: '#1B2A4A' }} data-testid="tdb-import-summary">
                  {tdbImportResult.imported} new, {tdbImportResult.updated} updated, {tdbImportResult.skipped} skipped (of {tdbImportResult.total_rows})
                </span>
              </div>
              {tdbImportResult.skipped_breakdown && (
                <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>
                  Skipped: {tdbImportResult.skipped_breakdown.already_match} already-match, {tdbImportResult.skipped_breakdown.all_zero} zero-amount.
                </p>
              )}
              {tdbImportResult.errors?.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {tdbImportResult.errors.slice(0, 5).map((e, i) => (
                    <p key={i} className="text-[10px] flex items-center gap-1" style={{ color: '#D4726A' }}>
                      <AlertTriangle size={10} /> Row {e.row}: {e.error}
                    </p>
                  ))}
                  {tdbImportResult.errors.length > 5 && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>+{tdbImportResult.errors.length - 5} more errors</p>}
                </div>
              )}
            </div>
          )}
          {tdbImportResult?.error && (
            <p className="text-xs" style={{ color: '#D4726A' }} data-testid="tdb-import-error">{tdbImportResult.error}</p>
          )}
        </div>
      )}

      {/* Date picker + today total */}
      <div className="flex items-center justify-between gap-3 bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-2">
          <Calendar size={16} style={{ color: '#2A7B88' }} />
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            className="text-sm font-medium border-0 p-0 focus:outline-none" style={{ color: '#1B2A4A' }} data-testid="daily-date-picker" />
        </div>
        <div className="text-right">
          <p className="text-[10px] font-medium uppercase" style={{ color: '#6B7280' }}>Day Total</p>
          <p className="text-lg font-bold" style={{ color: todayTotal > 0 ? '#2D8B5E' : '#9CA3AF' }} data-testid="day-total">{fmtCurrency(todayTotal)}</p>
        </div>
      </div>

      {/* Quick entry form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="daily-entry-form">
        {/* Outlet selector */}
        <div>
          <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Outlet *</label>
          <select value={form.outlet} onChange={e => handleOutletChange(e.target.value)} required
            className="w-full px-3 py-2.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="daily-outlet">
            <option value="">Select outlet</option>
            {outlets.filter(o => o.status === 'active').map((o, i) => <option key={i} value={o.name}>{o.name}{o.location ? ` (${o.location})` : ''}</option>)}
          </select>
        </div>

        {/* Sub-category fields (dynamic based on outlet) */}
        {subCats.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Revenue Breakdown</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {subCats.map(cat => (
                <div key={cat}>
                  <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>{cat}</label>
                  <input type="number" min="0" step="1" value={form.sub_amounts[cat] || ''}
                    onChange={e => handleSubAmountChange(cat, e.target.value)}
                    placeholder="0" className="w-full px-2.5 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
                    data-testid={`sub-${cat.toLowerCase().replace(/\s+/g, '-')}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Occupancy fields (for Accommodation) */}
        {hasOccupancy && (
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Occupancy ({roomConfig.total_rooms || 10} rooms: {roomConfig.suites || 7}S + {roomConfig.deluxe || 3}D)</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Suites occupied (of {roomConfig.suites || 7})</label>
                <input type="number" min="0" max={roomConfig.suites || 7} value={form.occupancy.suites_occupied || ''}
                  onChange={e => setForm(p => ({ ...p, occupancy: { ...p.occupancy, suites_occupied: e.target.value } }))}
                  className="w-full px-2.5 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="occupancy-suites" />
              </div>
              <div>
                <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Deluxe occupied (of {roomConfig.deluxe || 3})</label>
                <input type="number" min="0" max={roomConfig.deluxe || 3} value={form.occupancy.deluxe_occupied || ''}
                  onChange={e => setForm(p => ({ ...p, occupancy: { ...p.occupancy, deluxe_occupied: e.target.value } }))}
                  className="w-full px-2.5 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="occupancy-deluxe" />
              </div>
            </div>
          </div>
        )}

        {/* Total + Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Total Amount (INR) *{subCats.length > 0 ? ' (auto-calculated)' : ''}</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} required
              placeholder="e.g. 45000" min="0" step="1" readOnly={subCats.length > 0 && calcTotal(form.sub_amounts) > 0}
              className="w-full px-3 py-2.5 rounded-md border text-sm font-medium" style={{ borderColor: '#E5E1DC', backgroundColor: subCats.length > 0 && calcTotal(form.sub_amounts) > 0 ? '#F7F5F2' : '#fff' }} data-testid="daily-amount" />
          </div>
          <div>
            <label className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Notes</label>
            <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Optional" className="w-full px-3 py-2.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="submit" className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white"
            style={{ backgroundColor: '#1B2A4A' }} data-testid="daily-submit">
            <Plus size={14} /> Add Entry
          </button>
          <button type="button" onClick={repeatYesterday} className="flex items-center gap-1.5 px-3 py-2.5 rounded-md text-xs border hover:bg-gray-50"
            style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="repeat-yesterday">
            <Copy size={13} /> Yesterday
          </button>
        </div>
      </form>

      {/* Today's entries */}
      {entries.length > 0 && (
        <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <div className="px-4 py-2.5 border-b" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }}>
            <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>{selectedDate} — {entries.length} entries</span>
          </div>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {entries.map(e => (
              <div key={e.id} className="px-4 py-2.5" data-testid={`entry-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium cursor-pointer hover:text-[#2A7B88] hover:underline" style={{ color: '#1B2A4A' }}
                        onClick={() => navigate(`/division/${divId}/outlet/${encodeURIComponent(e.outlet)}`)}>{e.outlet}</p>
                      <button onClick={() => openDrill(e.outlet)} className="p-0.5 rounded hover:bg-gray-100"><Eye size={11} style={{ color: '#2A7B88' }} /></button>
                    </div>
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                      {e.entered_by}{e.notes && ` · ${e.notes}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold" style={{ color: '#2D8B5E' }}>{fmtCurrency(e.amount)}</span>
                    <button onClick={() => handleDelete(e.id)} className="p-1 rounded hover:bg-gray-100"><Trash2 size={13} style={{ color: '#D4726A' }} /></button>
                  </div>
                </div>
                {/* Sub-category breakdown if available */}
                {e.sub_amounts && Object.keys(e.sub_amounts).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(e.sub_amounts).map(([k, v]) => (
                      <span key={k} className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{k}: {fmtCurrency(v)}</span>
                    ))}
                  </div>
                )}
                {e.occupancy && e.occupancy.total_occupied > 0 && (
                  <p className="text-[9px] mt-1" style={{ color: '#2A7B88' }}>Occupancy: {e.occupancy.total_occupied}/{e.occupancy.total_rooms} rooms ({Math.round(e.occupancy.total_occupied / e.occupancy.total_rooms * 100)}%)</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aggregates */}
      <div className="bg-white rounded-lg border" style={{ borderColor: '#E5E1DC' }}>
        <button onClick={() => setShowAgg(!showAgg)} className="w-full flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} style={{ color: '#2A7B88' }} />
            <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Aggregates</span>
          </div>
          <div className="flex items-center gap-2">
            <select value={aggPeriod} onChange={e => { e.stopPropagation(); setAggPeriod(e.target.value); }}
              onClick={e => e.stopPropagation()} className="text-xs border rounded px-2 py-1" style={{ borderColor: '#E5E1DC' }}>
              <option value="daily">Today</option><option value="weekly">This Week</option>
              <option value="monthly">This Month</option><option value="quarterly">This Quarter</option>
              <option value="ytd">FY YTD</option>
            </select>
            {showAgg ? <ChevronUp size={14} style={{ color: '#6B7280' }} /> : <ChevronDown size={14} style={{ color: '#6B7280' }} />}
          </div>
        </button>
        {showAgg && aggregates && (
          <div className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}><p className="text-[10px] uppercase font-medium" style={{ color: '#6B7280' }}>Total</p><p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(aggregates.total)}</p></div>
              <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}><p className="text-[10px] uppercase font-medium" style={{ color: '#6B7280' }}>Entries</p><p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{aggregates.entry_count}</p></div>
              <div className="p-2 rounded" style={{ backgroundColor: '#F7F5F2' }}><p className="text-[10px] uppercase font-medium" style={{ color: '#6B7280' }}>Daily Avg</p><p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(aggregates.daily_average)}</p></div>
            </div>
            {aggregates.by_outlet?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase mb-1" style={{ color: '#6B7280' }}>By Outlet</p>
                {aggregates.by_outlet.map((o, i) => (
                  <div key={i} className="flex justify-between items-center text-xs py-1 border-b cursor-pointer hover:bg-gray-50" style={{ borderColor: '#F3F4F6' }} onClick={() => openDrill(o.outlet)}>
                    <span style={{ color: '#1B2A4A' }}>{o.outlet}</span>
                    <div className="flex items-center gap-2"><span className="font-medium" style={{ color: '#2D8B5E' }}>{fmtCurrency(o.total)}</span><Eye size={10} style={{ color: '#9CA3AF' }} /></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Outlet Drill-Down Panel */}
      {drillOutlet && drillData && (
        <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={() => setDrillOutlet(null)}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()} data-testid="outlet-drill-panel">
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
              <div>
                <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{drillData.outlet}</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>{drillData.date_from} to {drillData.date_to} · {drillData.entry_count} entries</p>
              </div>
              <button onClick={() => setDrillOutlet(null)}><X size={18} style={{ color: '#6B7280' }} /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Total + Channel Mix */}
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#F7F5F2' }}>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>Total</p>
                    <p className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>{fmtCurrency(drillData.total)}</p>
                  </div>
                  <p className="text-[11px]" style={{ color: '#6B7280' }}>{drillData.entry_count} entries</p>
                </div>
              </div>

              {/* Channel Mix — ranked, with percentages and a horizontal bar per channel */}
              {Object.entries(drillData.sub_totals || {}).filter(([, v]) => v > 0).length > 0 && (
                <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }} data-testid="channel-mix">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold" style={{ color: '#1B2A4A' }}>Channel Mix</p>
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{Object.entries(drillData.sub_totals).filter(([, v]) => v > 0).length} active</p>
                  </div>
                  {/* Stacked horizontal proportion bar */}
                  <div className="flex h-2 rounded-full overflow-hidden mb-3" style={{ backgroundColor: '#F3F4F6' }}>
                    {Object.entries(drillData.sub_totals)
                      .filter(([, v]) => v > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([k, v], i) => {
                        const pct = (v / drillData.total) * 100;
                        const colors = ['#2A7B88', '#378ADD', '#BA7517', '#7C3AED', '#D4726A', '#2D8B5E', '#F59E0B'];
                        return <div key={k} style={{ width: `${pct}%`, backgroundColor: colors[i % colors.length] }} title={`${k}: ${pct.toFixed(1)}%`} />;
                      })}
                  </div>
                  {/* Per-channel rows */}
                  <div className="space-y-1.5">
                    {Object.entries(drillData.sub_totals)
                      .sort(([, a], [, b]) => b - a)
                      .map(([k, v], i) => {
                        const pct = drillData.total > 0 ? (v / drillData.total) * 100 : 0;
                        const colors = ['#2A7B88', '#378ADD', '#BA7517', '#7C3AED', '#D4726A', '#2D8B5E', '#F59E0B'];
                        const isZero = v === 0;
                        return (
                          <div key={k} className="flex items-center justify-between text-xs" data-testid={`channel-${k.toLowerCase().replace(/\s+/g, '-')}`}>
                            <div className="flex items-center gap-2 flex-1">
                              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: isZero ? '#E5E7EB' : colors[i % colors.length] }} />
                              <span style={{ color: isZero ? '#9CA3AF' : '#1B2A4A' }}>{k}</span>
                              {isZero && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#BA7517' }}>inactive</span>}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-semibold tabular-nums" style={{ color: '#1B2A4A' }}>{fmtCurrency(v)}</span>
                              <span className="tabular-nums w-10 text-right" style={{ color: '#6B7280' }}>{pct.toFixed(1)}%</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
              {/* Date-wise table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                    <th className="text-left px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Date</th>
                    <th className="text-right px-2 py-2 font-medium" style={{ color: '#6B7280' }}>Total</th>
                    {(drillData.sub_categories || []).map(c => (
                      <th key={c} className="text-right px-2 py-2 font-medium whitespace-nowrap" style={{ color: '#6B7280' }}>{c}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                    {drillData.by_date.map(d => (
                      <tr key={d.date}>
                        <td className="px-2 py-1.5 font-medium" style={{ color: '#1B2A4A' }}>{d.date}</td>
                        <td className="px-2 py-1.5 text-right font-medium" style={{ color: '#2D8B5E' }}>{fmtCurrency(d.total)}</td>
                        {(drillData.sub_categories || []).map(c => (
                          <td key={c} className="px-2 py-1.5 text-right" style={{ color: '#6B7280' }}>{fmtCurrency(d.sub_amounts?.[c] || 0)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {drillData.by_date.length === 0 && <p className="text-xs text-center py-4" style={{ color: '#9CA3AF' }}>No entries yet for this outlet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
